<template>
  <ion-row v-if="isLoading">
    <ion-col>
      <p>Loading messages...</p>
    </ion-col>
  </ion-row>

  <ion-row v-else-if="!hasLoaded">
    <ion-col>
      <p>We couldn't load your messages. Please try again later.</p>
    </ion-col>
  </ion-row>

  <ion-row v-else-if="messages.length === 0">
    <ion-col>
      <p>{{ emptyText }}</p>
    </ion-col>
  </ion-row>

  <ion-row v-else>
    <ion-col>
      <ion-card v-for="message in messages" :key="message.id" color="light">
        <ion-card-content>
          <div v-if="message.claim" class="message-claim">
            {{ message.claim.product }} &middot; Request {{ message.claim.claim_no }}
          </div>
          <div class="message-meta">
            <span :style="{ color: branding.color_primary }">{{
              formatDate(new Date(message.sentAt))
            }}</span>
            <span v-if="newMessageIds.has(message.id)" class="unread-badge">New</span>
          </div>
          <!--
            message.body's merge-field values (e.g. claimant name/email) are HTML-encoded
            server-side before substitution (see vapcore's ClaimPlaceholderDataService) - only the
            staff-authored template markup itself is ever real HTML here, not a value a customer
            could have supplied, so v-html is safe.
          -->
          <div class="message-body" v-html="message.body"></div>
          <div v-if="message.showUploadPhotoButton" class="message-upload-action">
            <ion-button size="small" @click="$emit('upload-photo', message.claim.id)"
              >Upload Photo</ion-button
            >
          </div>
        </ion-card-content>
      </ion-card>
    </ion-col>
  </ion-row>
</template>

<script setup>
import { IonRow, IonCol, IonCard, IonCardContent, IonButton } from '@ionic/vue';
import { computed } from 'vue';
import { formatDate } from '@/composables/formatDate';
import { useBrandingStore } from '../store/branding';

const props = defineProps({
  /** Each message must already carry a `.claim` object with `.product`/`.claim_no`. */
  messages: {
    type: Array,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  /**
   * True once a fetch has actually completed (success or failure) — distinct from `isLoading`
   * so a failed fetch shows an error message instead of silently rendering as "no messages yet".
   */
  hasLoaded: {
    type: Boolean,
    default: true,
  },
  emptyText: {
    type: String,
    default: 'No messages yet.',
  },
  /**
   * Ids of messages that were unread when the page loaded them. Deliberately NOT derived from
   * `message.readAt` directly - the page marks messages read (updating readAt in the store)
   * moments after loading them, and a live readAt check would make the "New" badge disappear
   * almost instantly instead of staying for the rest of this page visit.
   */
  newMessageIds: {
    type: Array,
    default: () => [],
  },
});

const newMessageIds = computed(() => new Set(props.newMessageIds));

defineEmits(['upload-photo']);

const brandingStore = useBrandingStore();
const branding = brandingStore.brandCSS;
</script>

<style lang="scss" scoped>
ion-card {
  margin: 0 0 16px 0;
  border-radius: 0 14px 14px 14px;
}

.message-claim {
  font-size: 0.8rem;
  font-weight: bold;
  color: #252539;
  margin-bottom: 4px;
}

.message-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  margin-bottom: 8px;
}

.unread-badge {
  background: #ef7e31;
  color: #ffffff;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 0.75rem;
  font-weight: bold;
}

.message-body {
  color: #252539;
}

.message-upload-action {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}
</style>
